
import TeamsService from '@/Services/TeamsService'
import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useProgrammation } from './UseProgrammation'
import { useProject } from './UseProject'
import { useQuery } from './UseQuery'
import TeamsActions, { TEAMS_ACTION_TYPES } from '@/Redux/Actions/TeamsActions'
import ProjectsService from '@/Services/ProjectsService'
import { useLocation } from 'react-router-dom'

import History from '@/Helpers/History'
import { useAuth } from './UseAuth'
import { usePlan } from './UsePlan'
import Swal from 'sweetalert2'

interface Options {
  team_id: string
  mode?: 'create' | 'edit'
  noRedirect?: boolean
}

interface ContextValue {
  editingTeam: boolean
  errors: Error[]
  allInternalProfiles: any[]
  invites: any[]
  showTeamCreator: boolean
  showTeamMembersSelector: boolean
  teams: any[]
  teamSelected: any
  teamsLoaded: boolean
  users: any[]
  addInternalProfile: (internalProfile: any) => void
  addMe: () => Promise<void>
  cancelInvite: (user_id: string) => Promise<void>
  clearTeamsData: () => void
  clearErrors: () => void
  closeTeam: () => void
  createTeam: (name: string) => void
  deleteTeam: () => void
  inviteUser: (user_id: string) => Promise<void>
  inviteByMail: (email: string) => Promise<void>
  loadTeams: () => void
  moveUserToTeam: (user: any, toTeam: any) => Promise<void>
  moveProfileToTeam: (profile: any, toTeam: any) => Promise<void>
  removeUserFromTeam: (user_id: string, team_id: string) => Promise<void>
  removeProfileFromTeam: (profile_id: string, team_id: string) => void
  resendInvite: (user_id: string) => Promise<void>
  selectTeam: (team: any) => void
  setError: (error: Error) => void
  setShowTeamCreator: (show: boolean) => void
  setShowTeamMembersSelector: (show: boolean) => void
  setUserShowTasksMode: (user_id: string, mode: 'all' | 'only-their-tasks') => Promise<void>
  toggleAssistant: (user_id: string, action: 'add' | 'remove') => Promise<void>
  updateTeam: (name: string) => Promise<void>
  updateTeamField: (fieldName: string, value: string) => void
  unSetError: (field: string) => void
}

const TeamsContext = React.createContext<ContextValue | undefined>(undefined)

export const TeamsProvider = ({ children }: { children: React.ReactNode }) => {

  const dispatch = useDispatch()

  const { pathname } = useLocation()

  const query = useQuery();

  const { setProjectTeams } = usePlan()

  const { project } = useProject()

  const { userData } = useAuth()

  let team_id = pathname.split('/')[4]
  const isTeamRoute = pathname.split('/')[3] === 'teams'

  if (!team_id) {

    team_id = query.get("team_id") || '';
  }

  const [ loadingTeams, setLoadingTeams ] = useState(false)
  const [ teamsLoaded, setTeamsLoaded ] = useState(false)
  const [ teams, setTeams ] = useState<any[]>([])
  const [ teamSelected, setTeamSelected ] = useState<any | undefined>(undefined)
  const [ editingTeam, setEditingTeam ] = useState(false)
  const [ allInvites, setAllInvites ] = useState<any[]>([])
  const [ allUsers, setAllUsers ] = useState<any[]>([])
  const [ allInternalProfiles, setAllInternalProfiles ] = useState<any[]>([])
  const [ showTeamCreator, setShowTeamCreator ] = useState(false)
  const [ showTeamMembersSelector, setShowTeamMembersSelector ] = useState(false)
  const [ errors, setErrors ] = useState<any[]>([])

  const loadTeams = async () => {
    setLoadingTeams(true)

    const [ errTeams, data ] = await TeamsService.getResult(project._id)

    if (errTeams) return console.log(errTeams)

    setLoadingTeams(false)
    setTeams(data.data)
    setProjectTeams(data.data)
    setAllInternalProfiles(data.internalProfiles)
    setTeamsLoaded(true)
  }

  const selectTeam = (team: any) => {

    setTeamSelected(team)
  }

  const createTeam = async (name: string) => {
    const [err, data] = await TeamsService.createRow(name, project._id)

    if (err) return console.log(err)

    const newTeams = [...teams]
    newTeams.push(data)

    setTeams(newTeams)
    setProjectTeams(newTeams)
  }

  const updateTeam = async (name: string) => {

    await TeamsService.updateRow(name, teamSelected._id)

    const team = { ...teamSelected, name }

    setTeamSelected(team)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.name = name
      }

      return team
    })

    setTeams(newTeams)
    setProjectTeams(newTeams)
  }

  const deleteTeam = async () => {
    await TeamsService.deleteRow(teamSelected._id)

    const newTeams = [...teams].filter((team: any) => team._id !== teamSelected._id)

    setTeams(newTeams)
    setProjectTeams(newTeams)

    History.replace(`/project/${project._id}/teams`)

    setTeamSelected(undefined)
  }

  const moveUserToTeam = async (userToMove: any, toTeam: any) => {

    await TeamsService.moveUser(userToMove._id, teamSelected._id, toTeam._id)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.users = team.users.filter((user: any) => user._id !== userToMove._id)
      }

      if (team._id === toTeam._id) {
        team.users.push(userToMove)
      }

      return team
    })

    setTeams(newTeams)

    const oldTeam = newTeams.find((team: any) => team._id === teamSelected._id)

    if (oldTeam.users.length === 0) {

      const newToTeam = newTeams.find((team: any) => team._id === toTeam._id)

      setTeamSelected(newToTeam)
    } else {

      setTeamSelected(oldTeam)
    }
  }

  const moveProfileToTeam = async (profile: any, toTeam: any) => {

    const teamTarget = teams.find((team: any) => team._id === toTeam._id)

    const isAlreadyInTeam = teamTarget.internalProfiles.find((user: any) => user._id === profile._id)

    if (isAlreadyInTeam) {

      Swal.fire({
        title: 'Error',
        text: 'El perfil ya está en ese equipo',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        width: '500px',
      })
      return
    }

    const [err] = await TeamsService.moveProfile(profile._id, teamSelected._id, toTeam._id)

    if (err) return console.log(err)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.internalProfiles = team.internalProfiles.filter((user: any) => user._id !== profile._id)
      }

      if (team._id === toTeam._id) {
        team.internalProfiles.push(profile)
      }

      return team
    })

    setTeams(newTeams)

    const oldTeam = newTeams.find((team: any) => team._id === teamSelected._id)

    if (oldTeam.internalProfiles.length === 0) {

      const newToTeam = newTeams.find((team: any) => team._id === toTeam._id)

      setTeamSelected(newToTeam)
    } else {

      setTeamSelected(oldTeam)
    }
  }

  const setUserShowTasksMode = async (user_id: string, mode: 'all' | 'only-their-tasks') => {

    await TeamsService.hideTasks(project._id, user_id, mode === 'only-their-tasks')

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.users.forEach((user: any) => {

          if (user._id === user_id) {
            user.showOnlyMyTasks = mode === 'only-their-tasks'
          }
        })
      }

      return team
    })

    setTeams(newTeams)
  }

  const removeUserFromTeam = async (user_id: string, team_id: string) => {

    await TeamsService.removeUser(user_id, team_id)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.users = team.users.filter((user: any) => user._id !== user_id)
      }

      return team
    })

    console.log('newTeams', newTeams)

    setTeams(newTeams)
  }

  const removeProfileFromTeam = (profile_id: string) => {

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.internalProfiles = team.internalProfiles.filter((profile: any) => profile._id !== profile_id)
      }

      return team
    })

    setTeams(newTeams)

    const team = { ...teamSelected }
    team.internalProfiles = team.internalProfiles.filter((profile: any) => profile._id !== profile_id)

    setTeamSelected(team)
  }

  const inviteUser = async (user: any) => {

    await TeamsService.inviteUser(user._id, teamSelected._id)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.invites.push(user)
      }

      return team
    })

    setTeams(newTeams)
  }

  const cancelInvite = async (user_id: string) => {

    await TeamsService.cancelInvite(project._id, teamSelected._id, user_id)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.invites = team.invites.filter((user: any) => user._id !== user_id)
      }

      return team
    })

    setTeams(newTeams)
  }

  const resendInvite = async (user_id: string) => {

    await TeamsService.resendInvite(project._id, teamSelected._id, user_id)
  }

  const inviteByMail = async (email: string) => {

    await TeamsService.mailInviteUser(email, teamSelected._id)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.mailInvites.push({
          _id: email,
          name: email,
          surname: '',
          isInTeam: true,
          hasPendingInvite: true,
          showOnlyMyTasks: false,
        })
      }

      return team
    })

    setTeams(newTeams)
  }

  const toggleAssistant = async (user_id: string, action: 'add' | 'remove') => {

    const service = action === 'add'
              ? ProjectsService.addAssistant
              : ProjectsService.removeAssistant

    const [ err ] = await service(project._id, user_id)

    if (err) return console.log(err)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {

        team.users.forEach((user: any) => {

          if (user._id === user_id) {

            user.isAssistant = action === 'add'
          }
        })
      }

      return team
    })

    setTeams(newTeams)

    const updatedTeam = newTeams.find((team: any) => team._id === teamSelected._id)
    setTeamSelected(updatedTeam)
  }

  const addInternalProfile = async (internalProfile: any) => {

    const team = {...teamSelected}

    team.internalProfiles.push(internalProfile)

    setTeamSelected(team)

    const alreadyInAllList = allInternalProfiles.find((prof: any) => prof._id === internalProfile._id)

    if (alreadyInAllList) return

    setAllInternalProfiles([...allInternalProfiles, internalProfile])
  }

  const addMe = async () => {

    await TeamsService.addMe(teamSelected._id)

    const newTeams = [...teams].map((team: any) => {

      if (team._id === teamSelected._id) {
        team.users.push(userData)
      }

      return team
    })

    setTeams(newTeams)
  }

  const clearTeamsData = () => {
    setTeams([])
    setTeamSelected(undefined)
    setAllInternalProfiles([])
    setAllInvites([])
    setAllUsers([])
    setTeamsLoaded(false)
  }

  useEffect(() => {

    if (!teamsLoaded) return

    const [invites, users] = teams.reduce((acc: any[], team: any) => {

      const { invites, users, internalProfiles } = team

      if (invites) acc[0] = acc[0].concat(invites)
      if (users) acc[1] = acc[1].concat(users)
      if (internalProfiles) acc[2] = acc[2].concat(internalProfiles)

      return acc
    }, [[], [], []])

    setAllInvites(invites)
    setAllUsers(users)
  }, [teamsLoaded, teams])

  useEffect(() => {
    if (teamSelected || !teams.length || !isTeamRoute) return


    if (!team_id) {

      setTeamSelected(teams[0])
    } else {

      const team = teams.find((team: any) => team._id === team_id)

      if (team) {

        console.log('Auto selecting team', team_id)
        setTeamSelected(team)
      }
    }
  }, [teams, team_id, isTeamRoute, teamSelected])

  const value: ContextValue = {
    editingTeam,
    errors,
    allInternalProfiles,
    invites: allInvites,
    showTeamCreator,
    showTeamMembersSelector,
    teams,
    teamSelected,
    teamsLoaded,
    users: allUsers,
    addInternalProfile,
    addMe,
    cancelInvite,
    clearTeamsData,
    clearErrors: () => {},
    closeTeam: () => {},
    createTeam,
    deleteTeam,
    inviteUser,
    inviteByMail,
    loadTeams,
    moveUserToTeam,
    moveProfileToTeam,
    removeUserFromTeam,
    removeProfileFromTeam,
    resendInvite,
    selectTeam,
    setError: () => {},
    setShowTeamCreator,
    setShowTeamMembersSelector,
    setUserShowTasksMode,
    toggleAssistant,
    updateTeam,
    updateTeamField: () => {},
    unSetError: () => {},
  }

  return (
    <TeamsContext.Provider value={value}>
      {children}
    </TeamsContext.Provider>
  )
}

export const useTeams = (): ContextValue => {

  const context = useContext(TeamsContext)

  if (context === undefined) {

    throw new Error('useTeams must be used within a TeamsProvider')
  }

  return context
}
